import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTypedTranslation } from '@/lib/i18n/hooks';
import { useBrandSelection } from './useBrandSelection';
import { toast } from 'sonner';

interface BrandTerminology {
  id: string;
  brand_id: string;
  category: string;
  technical_term: string;
  business_name_singular: string;
  business_name_plural: string;
  context_overrides?: any; // Using any for JSON compatibility
  enabled?: boolean;
  created_at: string;
  updated_at: string;
}

type TerminologyOptions = {
  plural?: boolean;
  context?: string;
  fallback?: string;
};

const DEFAULT_TERMINOLOGY = {
  tags: { singular: 'Tag', plural: 'Tags' },
  more_tags: { singular: 'More Tag', plural: 'More Tags' },
  tag3: { singular: 'Tag3', plural: 'Tag3s' },
  categories: { singular: 'Category', plural: 'Categories' },
  subcategories: { singular: 'Subcategory', plural: 'Subcategories' },
  foundational_tasks: { singular: 'Foundational Task', plural: 'Foundational Tasks' },
  growth_tasks: { singular: 'Growth Task', plural: 'Growth Tasks' },
  recurring_tasks: { singular: 'Recurring Task', plural: 'Recurring Tasks' },
  source_my_own_initiative: { singular: 'My Own Initiative', plural: 'My Own Initiative' },
  source_client_request: { singular: 'Client Request', plural: 'Client Requests' },
  source_bug_report: { singular: 'Bug Report', plural: 'Bug Reports' },
  source_feature_request: { singular: 'Feature Request', plural: 'Feature Requests' },
  source_technical_debt: { singular: 'Technical Debt', plural: 'Technical Debt' },
  source_recurring_rule: { singular: 'Recurring Rule', plural: 'Recurring Rules' },
  source_2y3x: { singular: '2Y3X Methodology', plural: '2Y3X Methodology' },
  // Business Metrics Types
  metric_leads: { singular: 'Lead', plural: 'Leads' },
  metric_orders: { singular: 'Order', plural: 'Orders' },
  metric_sales: { singular: 'Sale', plural: 'Sales' },
  metric_conversions: { singular: 'Conversion', plural: 'Conversions' },
};

// Smart pluralization function
const pluralize = (word: string): string => {
  // Handle irregular plurals
  const irregulars: Record<string, string> = {
    'Geography': 'Geographies',
    'Stage': 'Stages',
    'Type': 'Types',
    'Category': 'Categories',
    'Tag': 'Tags',
  };

  if (irregulars[word]) {
    return irregulars[word];
  }

  // Basic pluralization rules
  if (word.endsWith('y')) {
    return word.slice(0, -1) + 'ies';
  }
  if (word.endsWith('s') || word.endsWith('sh') || word.endsWith('ch') || word.endsWith('x') || word.endsWith('z')) {
    return word + 'es';
  }
  return word + 's';
};

export const useBrandTerminology = () => {
  const { currentBrandId } = useBrandSelection();
  const { t } = useTypedTranslation();
  const queryClient = useQueryClient();

  const { data: terminology, isLoading } = useQuery({
    queryKey: ['brand-terminology', currentBrandId],
    queryFn: async (): Promise<BrandTerminology[]> => {
      if (!currentBrandId) return [];
      
      const { data, error } = await supabase
        .from('brand_business_terminology')
        .select('*')
        .eq('brand_id', currentBrandId);

      if (error) throw error;
      return data || [];
    },
    enabled: !!currentBrandId,
  });

  const createTerminologyMutation = useMutation({
    mutationFn: async (newTerminology: Omit<BrandTerminology, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('brand_business_terminology')
        .insert(newTerminology)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-terminology', currentBrandId] });
      toast.success('Business terminology updated successfully');
    },
    onError: (error) => {
      console.error('Error creating terminology:', error);
      toast.error('Failed to update terminology');
    },
  });

  const updateTerminologyMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<BrandTerminology> }) => {
      const { data, error } = await supabase
        .from('brand_business_terminology')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-terminology', currentBrandId] });
      toast.success('Business terminology updated successfully');
    },
    onError: (error) => {
      console.error('Error updating terminology:', error);
      toast.error('Failed to update terminology');
    },
  });

  const getTerminology = (technicalTerm: keyof typeof DEFAULT_TERMINOLOGY, options: TerminologyOptions = {}): string => {
    const { plural = false, context, fallback } = options;
    
    // Find custom terminology for this brand
    const customTerm = terminology?.find(term => term.technical_term === technicalTerm);
    
    if (customTerm) {
      // Check for context-specific overrides first
      if (context && customTerm.context_overrides?.[context]) {
        return customTerm.context_overrides[context];
      }
      
      // Return singular or plural based on request
      if (plural) {
        return customTerm.business_name_plural;
      }
      return customTerm.business_name_singular;
    }

    // Fallback to default terminology with i18n support
    const defaultTerm = DEFAULT_TERMINOLOGY[technicalTerm];
    if (defaultTerm) {
      const term = plural ? defaultTerm.plural : defaultTerm.singular;
      return fallback || term;
    }

    // Last resort fallback
    return fallback || (plural ? pluralize(technicalTerm) : technicalTerm);
  };

  const getFeatureEnabled = (technicalTerm: keyof typeof DEFAULT_TERMINOLOGY): boolean => {
    const customTerm = terminology?.find(term => term.technical_term === technicalTerm);
    return customTerm?.enabled ?? true; // Default to enabled if not found
  };

  const createTerminology = createTerminologyMutation.mutate;
  const updateTerminology = updateTerminologyMutation.mutate;

  return {
    terminology,
    isLoading,
    getTerminology,
    getFeatureEnabled,
    createTerminology,
    updateTerminology,
    isCreating: createTerminologyMutation.isPending,
    isUpdating: updateTerminologyMutation.isPending,
  };
};